import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Eye, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { scenariosApi } from '../../api/scenarios';
import '../operations.css';

const safeScenarioSrcDoc = (html: string) => {
  const csp = '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src data:; style-src \'unsafe-inline\'">';
  return `${csp}${html}`;
};

export function ScenarioList() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const { data: scenarios, isLoading, isError } = useQuery({
    queryKey: ['scenarios'],
    queryFn: async () => (await scenariosApi.getAll()).data,
  });
  const { data: scenario, isLoading: detailLoading, isError: detailError } = useQuery({
    queryKey: ['scenario', selectedId],
    queryFn: async () => (await scenariosApi.getById(selectedId!)).data,
    enabled: selectedId !== null,
  });
  const approveMutation = useMutation({
    mutationFn: (id: number) => scenariosApi.approve(id),
    onSuccess: async response => {
      queryClient.setQueryData(['scenario', response.data.id], response.data);
      await queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      setNotice('Scenario approved for future assessments.');
      setActionError('');
    },
    onError: () => { setActionError('Could not approve scenario. Check your access and refresh before retrying.'); setNotice(''); },
  });

  function openReview(id: number) {
    setSelectedId(id);
    setActionError('');
    setNotice('');
  }

  function closeReview() {
    if (!approveMutation.isPending) setSelectedId(null);
  }

  return <div className="operations-page space-y-6">
    <div><p className="operations-eyebrow">Simulation library</p><h1 className="text-2xl font-bold text-gray-900">Scenarios</h1><p className="text-gray-500 mt-1">Review sanitized, incident-derived scenarios before they become available to assessments.</p></div>
    <Card><div className="flex items-start gap-3"><ShieldAlert className="mt-1 text-indigo-600" size={20} /><p className="text-sm text-gray-600">Scenarios are created from reported messages so their source can be reviewed. A scenario must be approved before assessment use. The current API does not support editing or deleting scenarios.</p></div></Card>
    {notice && <p role="status" className="text-green-700">{notice}</p>}
    {actionError && selectedId === null && <p role="alert" className="text-red-700">{actionError}</p>}
    {isError && <p role="alert" className="text-red-700">Could not load scenarios. Check your access and retry.</p>}
    <Card>
      {isLoading ? <p role="status">Loading scenarios…</p> : isError ? null : !scenarios?.length ? <p>No scenarios available.</p> :
        <div className="overflow-x-auto"><table className="w-full text-sm text-left"><thead><tr className="border-b"><th className="p-3">Scenario</th><th className="p-3">Skill</th><th className="p-3">Kind</th><th className="p-3">Status</th><th className="p-3">Source</th><th className="p-3" /></tr></thead>
          <tbody>{scenarios.map(item => <tr key={item.id} className="border-b">
            <td className="p-3 font-medium">{item.name}</td><td className="p-3">{item.skill_tag}</td><td className="p-3">{item.kind === 'threat' ? 'Threat simulation' : 'Benign control'}</td>
            <td className="p-3"><span className={`badge ${item.status === 'approved' ? 'badge-green' : 'badge-yellow'}`}>{item.status === 'approved' ? 'Approved' : 'Draft'}</span></td>
            <td className="p-3"><Link className="text-indigo-700 underline" to={`/reported-messages?sourceId=${item.source_reported_message_id}`}>View source report</Link></td>
            <td className="p-3 text-right"><Button type="button" variant="secondary" size="sm" onClick={() => openReview(item.id)}><Eye size={14} /> Review {item.name}</Button></td>
          </tr>)}</tbody></table></div>}
    </Card>

    <Modal isOpen={selectedId !== null} onClose={closeReview} title="Review scenario" size="xl">
      {detailLoading && <p role="status">Loading scenario…</p>}
      {detailError && <p role="alert" className="text-red-700">Could not load this scenario. Close the review and retry.</p>}
      {scenario && <div className="space-y-4">
        <div><p className="text-sm text-gray-500">{scenario.kind === 'threat' ? 'Threat simulation' : 'Benign control'} · {scenario.skill_tag} · Version {scenario.version}</p><h2 className="text-lg font-semibold mt-1">{scenario.name}</h2><p className="font-medium mt-3">{scenario.subject}</p></div>
        <div><h3 className="font-medium mb-1">Plain text content</h3><pre className="whitespace-pre-wrap rounded border bg-gray-50 p-3 text-sm">{scenario.text}</pre></div>
        {scenario.html && <div><h3 className="font-medium mb-1">Sandboxed HTML preview</h3><iframe title="Scenario HTML preview" sandbox="" referrerPolicy="no-referrer" srcDoc={safeScenarioSrcDoc(scenario.html)} className="w-full min-h-56 rounded border bg-white" /></div>}
        <p><Link className="text-indigo-700 underline" to={`/reported-messages?sourceId=${scenario.source_reported_message_id}`} onClick={closeReview}>View source report</Link></p>
        {scenario.status === 'approved' ? <p className="flex items-center gap-2 text-green-700"><CheckCircle2 size={16} />Approved by {scenario.reviewed_by || 'reviewer'}</p> : <div className="space-y-2"><p className="text-sm text-amber-800">Draft. Review the source and sanitized content before approving this scenario for future assessments.</p>{actionError && <p role="alert" className="text-red-700">{actionError}</p>}<Button type="button" loading={approveMutation.isPending} disabled={approveMutation.isPending} onClick={() => approveMutation.mutate(scenario.id)}>Approve scenario</Button></div>}
        {notice && <p role="status" className="text-green-700">{notice}</p>}
      </div>}
    </Modal>
  </div>;
}
